import React, { useRef, useEffect } from 'react'
import { useGLTF } from '@react-three/drei'
import { useThree } from '@react-three/fiber'
import { Controls, SceneZone, PhysicsObjects, Media, useScene, SceneLighting, useFixedAnimations } from '@webcrafter/common'

/**
 * Props for SceneXyz3D component
 * @typedef {Object} SceneXyz3DProps
 * @property {string} path - Path to the GLB file
 * @property {Function} setShowPopup - Function to control popup visibility
 * @property {Function} setPopupContent - Function to set popup content
 * @property {boolean} isDebugging - Whether debug mode is enabled
 * @property {Function} setXyzAPI - Callback to expose the API to parent
 * @property {React.ReactNode} [children] - Optional child components
 */

/**
 * Main 3D scene component that loads and renders a GLB model.
 * Handles camera controls, animations, interactions, and orbit navigation.
 * 
 * @param {SceneXyz3DProps} props
 */
export const SceneXyz3D = ({
  path,
  setShowPopup,
  setPopupContent,
  isDebugging,
  children
}) =>
{
  const { camera, scene: threeScene } = useThree()
  const { scene, animations } = useGLTF(path)
  const { mixer, actions } = useFixedAnimations(animations, scene)

  const controlsRef = useRef(null)

  // Get scene state and initialization function
  const {
    sceneManager,
    interactionManager,
    initializeManagers
  } = useScene()

  // Initialize managers when ready
  useEffect(() =>
  {
    if (controlsRef.current && scene)
    {
      initializeManagers({
        scene,
        controls: controlsRef.current,
        animations,
        actions,
        mixer,
        camera,
        setShowPopup,
        setPopupContent
      })
    }
  }, [
    controlsRef.current,
    scene,
    initializeManagers,
    animations,
    actions,
    mixer,
    camera,
    setShowPopup,
    setPopupContent
  ])

  return (
    <>
      <Controls innerRef={controlsRef} />

      <primitive object={scene}>
        {/* Render scene zones for interactables */}
        {controlsRef.current && sceneManager && sceneManager.getSceneZones && (
          sceneManager.getSceneZones().map((zoneData, index) => (
            <SceneZone
              key={`zone-${zoneData.name || index}`}
              interactionManager={interactionManager}
              isDebugging={isDebugging}
              object={zoneData}
            />
          ))
        )}
      </primitive>

      {/* Physics simulation objects */}
      {sceneManager && (
        <PhysicsObjects
          debug={isDebugging}
          sceneManager={sceneManager}
          interactionManager={interactionManager}
          isDebugging={isDebugging}
        />
      )}

      {/* Media elements (video, audio) */}
      <Media
        sceneManager={sceneManager}
        interactionManager={interactionManager}
      />

      {children}

      {/* Scene lighting (environment, default lights) */}
      <React.Suspense fallback={null}>
        <SceneLighting scene={scene} sceneManager={sceneManager} />
      </React.Suspense>
    </>
  )
}

export default SceneXyz3D
